/**
 * PATH: packages/ui/src/kanban/components/settings-home/kanbanSettingsHost.ts
 * FILE_ID: UI-KANBAN-SETTINGS-HOST-001
 * PURPOSE: Session host so All Kanban default boards reuse the live API.
 * ROLE IN ARCHITECTURE: Settings-home registry. Not a second claim authority.
 * MAIN EXPORTS: registerKanbanSettingsHost, resolveKanbanSettingsHost
 * NON-RESPONSIBILITIES: Does not create KanbanApi or persist prefs.
 * NOTES FOR FUTURE AI: Board view still registers this. Gear Settings must
 *   resolve via the kanban-host API provider when no board has mounted.
 */

import type { KanbanApi } from '../../api'

export const KANBAN_OPEN_SHORTCUTS_EVENT = 'quotech-kanban-open-shortcuts'

export type KanbanSettingsHost = {
  api: KanbanApi
  organisationId: number
}

let host: KanbanSettingsHost | null = null
let apiProvider: (() => KanbanApi) | null = null

/** Remember the live Kanban API for Settings-hub default-board pickers. */
export function registerKanbanSettingsHost(next: KanbanSettingsHost | null): void {
  host = next
}

/** Current host, or null until a board shell or provider bind has run. */
export function getKanbanSettingsHost(): KanbanSettingsHost | null {
  return host
}

/** Kanban-host boot supplies the shared API so gear Settings need no board. */
export function registerKanbanSettingsApiProvider(
  next: (() => KanbanApi) | null,
): void {
  apiProvider = next
}

/** Positive organisation id from settings props or a board host. */
export function parseKanbanSettingsOrgId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return undefined
}

/**
 * Prefer the board-mounted host. Otherwise bind the boot API provider
 * plus the current organisation so default-board pickers can load.
 */
export function resolveKanbanSettingsHost(
  organisationId?: unknown,
): KanbanSettingsHost | null {
  const existing = getKanbanSettingsHost()
  if (existing?.api && existing.organisationId > 0) return existing
  const api = existing?.api ?? apiProvider?.()
  const org = existing?.organisationId ?? parseKanbanSettingsOrgId(organisationId)
  if (!api || org == null) return null
  const next = { api, organisationId: org }
  registerKanbanSettingsHost(next)
  return next
}

/** Ask the open board shell to show the shortcuts sheet. */
export function requestKanbanShortcutsOpen(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(KANBAN_OPEN_SHORTCUTS_EVENT))
}
